import { useQuery } from "@tanstack/react-query";
import type { VariantCost } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseVariantCostsQueryParams = {
  storeSlug: string;
  productId: string;
  enabled?: boolean;
};

/**
 * Seller-only per-variant goods cost (for profit/margin). Fetched separately
 * from the product view — cost is deliberately never part of the buyer-shared
 * `ProductVariantView`. Only live (non-tombstoned) variants are returned.
 */
export function useVariantCostsQuery({
  storeSlug,
  productId,
  enabled = true,
}: UseVariantCostsQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.productVariantCosts(storeSlug, productId),
    queryFn: () =>
      apiJsonClient<VariantCost[]>(
        endpoints.seller.productVariantCosts(productId),
      ),
    enabled: enabled && storeSlug.length > 0 && productId.length > 0,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
